import {
  useCallback, useState,
} from 'react';
import { getUserSpreadsheetImportBroadcast } from '~/_shared/utils/api/broadcast.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type {
  CreateMapErrorResponse, CreateMapSuccessResponse,
} from '../map.repository';
import {
  createMapFromInput, getGeneralCreateMapErrorMessage,
} from './createNewMap.helpers';

export const useCreateMapFromInput = (clientId: number | null, userId: number | null) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [t] = useTranslation();

  const resetState = useCallback(() => {
    setIsLoading(false);
    setErrorMessage('');
  }, []);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const createMapFromTextInput = useCallback(async (
    input: string,
    mapName: string,
    mapDescription: string,
    onCreateMapSuccess: (mapId: number) => void,
  ) => {
    if (!clientId) {
      return;
    }

    setIsLoading(true);

    try {
      const newMap = await createMapFromInput(clientId, input, mapName, mapDescription) as CreateMapSuccessResponse;

      const onSpreadsheetUploadProcessDone = () => {
        onCreateMapSuccess(newMap.data.id);
      };

      getUserSpreadsheetImportBroadcast(e => {
        if (e.userId === userId) {
          for (const spreadsheet of newMap.data.spreadsheets) {
            const spreadsheetIds = spreadsheet.real_spreadsheets.map(item => item.real_spreadsheet_id);
            if (spreadsheetIds.includes(e.spreadsheetId)) {
              onSpreadsheetUploadProcessDone();
              return;
            }
          }
        }
      }, () => {
        setErrorMessage(t('Something went wrong, please try again.'));
        setIsLoading(false);
      });
    }
    catch (e) {
      setErrorMessage(getGeneralCreateMapErrorMessage(e as CreateMapErrorResponse, t));
      setIsLoading(false);
    }
  }, [t, clientId, userId]);

  return {
    isLoading,
    resetState,
    errorMessage,
    clearErrorMessage,
    createMapFromTextInput,
  };
};
